import React, { useState, useEffect } from 'react';
import Layout from 'antd/lib/layout';
import notification from 'antd/lib/notification';
import Button from 'antd/lib/button';
import { Switch, Route } from 'react-router-dom';
import { validateVersion, createVersion, getBuildingList } from 'parity-network-requests';
import { config } from '../../config';
import { SingleBuildingHome } from '../SingleBuildingHome';
import { MultiBuildingHome } from '../MultiBuildingHome';
import { SavingsPage } from '../SavingsPage';
import { MonitoringPage } from '../MonitoringPage';
import { AlertsPage } from '../AlertsPage';
import { SettingsPage } from '../SettingsPage';
import { HelpPage } from '../HelpPage';
import { PageWrapper } from '../PageWrapper';
import { PageNotFound } from '../PageNotFound';

import lightVars from '../../light.json';
import darkVars from '../../dark.json';

const { REACT_APP_BUILD_ENV, REACT_APP_ID } = config;

export const Main = () => {
  const { Content } = Layout;

  const [buildings, setBuildings] = useState([]);
  const [selectedBuildingName, setSelectedBuildingName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [theme, setTheme] = useState('');
  const selectedBuildingFromSession = sessionStorage.getItem('selectedBuilding');

  const getSelectedBuildingName = (buildingsList) => {
    const building = selectedBuildingFromSession || '';

    if (!building) {
      return '';
    }

    const selectedBuilding = buildingsList.filter((buildingObj) => buildingObj.identifier === building)[0] || '';

    return selectedBuilding.name;
  };

  const refresh = () => {
    window.location.reload();
  };

  const changeTheme = () => {
    let vars = {};
    let newTheme = '';

    if (theme === 'light') {
      vars = darkVars;
      newTheme = 'dark';
    } else {
      vars = lightVars;
      newTheme = 'light';
    }

    localStorage.setItem('app-theme', JSON.stringify(vars));
    localStorage.setItem('theme-name', newTheme);
    window.less.modifyVars(vars);
    setTheme(newTheme);
  };

  const checkVersion = async () => {
    const versionNumber = process.env.REACT_APP_VERSION;

    const versionValidateObj = await validateVersion(REACT_APP_BUILD_ENV, versionNumber, REACT_APP_ID);

    const { is_latest: isLatest, should_create: shouldCreate } = versionValidateObj;

    if (!isLatest) {
      if (shouldCreate) {
        createVersion(REACT_APP_BUILD_ENV, true, versionNumber, REACT_APP_ID);
      } else {
        // Refresh
        const btn = (
          <Button type="primary" size="small" onClick={() => refresh()}>
            Refresh
          </Button>
        );
        notification.info({
          placement: 'bottomLeft',
          duration: 0,
          style: { borderRadius: '8px' },
          btn,
          message: `Your pi is getting stale`,
          description: 'To ensure you have a freshly baked pi, please click the refresh button',
        });
      }
    }
  };

  useEffect(() => {
    async function fetchData() {
      const buildingsList = await getBuildingList(REACT_APP_BUILD_ENV);
      setBuildings(buildingsList);
      const buildingName = getSelectedBuildingName(buildingsList);
      setSelectedBuildingName(buildingName);
      setIsLoading(false);
    }

    const getCurrentTheme = () => {
      const currentTheme = localStorage.getItem('theme-name');

      if (currentTheme === 'light') {
        localStorage.setItem('app-theme', JSON.stringify(lightVars));
        window.less.modifyVars(lightVars);
        setTheme(currentTheme);
      } else if (currentTheme === 'dark') {
        localStorage.setItem('app-theme', JSON.stringify(darkVars));
        window.less.modifyVars(darkVars);
        setTheme(currentTheme);
      } else {
        localStorage.setItem('theme-name', 'light');
        localStorage.setItem('app-theme', JSON.stringify(lightVars));
        window.less.modifyVars(lightVars);
        setTheme('light');
      }
    };

    getCurrentTheme();
    checkVersion();
    fetchData();
  }, [selectedBuildingFromSession]);

  const updateBuildings = (updatedBuildings) => {
    setBuildings(updatedBuildings);
  };

  if (isLoading) {
    return null;
  }

  return (
    <>
      <Layout>
        <Content style={{ height: '100vh' }}>
          <Switch>
            <Route
              exact
              path="/"
              render={() => {
                return <MultiBuildingHome updateBuildings={updateBuildings} theme={theme} changeTheme={changeTheme} />;
              }}
            />
            <Route
              exact
              path="/home/:building"
              render={() => {
                return (
                  <PageWrapper
                    category="home"
                    buildings={buildings}
                    selectedBuildingName={selectedBuildingName}
                    theme={theme}
                    changeTheme={changeTheme}
                  >
                    <SingleBuildingHome buildingName={selectedBuildingName} buildings={buildings} theme={theme} />
                  </PageWrapper>
                );
              }}
            />
            <Route
              exact
              path="/savings/:building"
              render={(routeProps) => {
                return (
                  <PageWrapper
                    category="savings"
                    buildings={buildings}
                    selectedBuildingName={selectedBuildingName}
                    theme={theme}
                    changeTheme={changeTheme}
                  >
                    <SavingsPage
                      theme={theme}
                      building={routeProps.match.params.building}
                      buildingName={selectedBuildingName}
                    />
                  </PageWrapper>
                );
              }}
            />
            <Route
              exact
              path="/monitoring/:building"
              render={() => {
                return (
                  <PageWrapper
                    category="monitoring"
                    buildings={buildings}
                    selectedBuildingName={selectedBuildingName}
                    theme={theme}
                    changeTheme={changeTheme}
                  >
                    <MonitoringPage theme={theme} buildingName={selectedBuildingName} />
                  </PageWrapper>
                );
              }}
            />
            <Route
              exact
              path="/alerts/:building"
              render={() => {
                return (
                  <PageWrapper
                    category="alerts"
                    buildings={buildings}
                    selectedBuildingName={selectedBuildingName}
                    theme={theme}
                    changeTheme={changeTheme}
                  >
                    <AlertsPage theme={theme} buildingName={selectedBuildingName} />
                  </PageWrapper>
                );
              }}
            />
            <Route
              exact
              path="/settings"
              render={() => {
                return (
                  <PageWrapper
                    category="settings"
                    buildings={buildings}
                    selectedBuildingName={selectedBuildingName}
                    theme={theme}
                    changeTheme={changeTheme}
                  >
                    <SettingsPage theme={theme} />
                  </PageWrapper>
                );
              }}
            />
            <Route
              exact
              path="/help"
              render={() => {
                return (
                  <PageWrapper
                    category="help"
                    buildings={buildings}
                    selectedBuildingName={selectedBuildingName}
                    theme={theme}
                    changeTheme={changeTheme}
                  >
                    <HelpPage theme={theme} />
                  </PageWrapper>
                );
              }}
            />
            <Route
              path="*"
              render={() => {
                return (
                  <PageWrapper
                    category="page not found"
                    buildings={buildings}
                    selectedBuildingName={selectedBuildingName}
                    theme={theme}
                    changeTheme={changeTheme}
                  >
                    <PageNotFound theme={theme} />
                  </PageWrapper>
                );
              }}
            />
          </Switch>
        </Content>
      </Layout>
    </>
  );
};
